import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import axios from 'axios';

dayjs.locale('de');

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const BlockedDates = () => {
  const [tabValue, setTabValue] = useState(0);
  const [blockedDates, setBlockedDates] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for adding blocked date
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState('Feiertag');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Load blocked dates
      const datesResponse = await axios.get(`${API_BASE_URL}/blocked_dates/manage_dates.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Load weekdays
      const weekdaysResponse = await axios.get(`${API_BASE_URL}/blocked_dates/manage_weekdays.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (datesResponse.data.success) {
        setBlockedDates(datesResponse.data.data);
      }
      
      if (weekdaysResponse.data.success) {
        setWeekdays(weekdaysResponse.data.data);
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!selectedDate) {
      alert('Bitte wählen Sie ein Datum aus');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/blocked_dates/manage_dates.php`,
        {
          blocked_date: selectedDate.format('YYYY-MM-DD'),
          reason: reason,
          is_active: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setDialogOpen(false);
        setSelectedDate(null);
        setReason('Feiertag');
        loadData();
      }
    } catch (err) {
      if (err.response?.status === 409 || err.response?.data?.message?.includes('Duplicate')) {
        setError('Dieses Datum ist bereits blockiert');
      } else {
        setError('Fehler beim Hinzufügen des Datums');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlockedDate = async (id) => {
    if (!window.confirm('Möchten Sie dieses blockierte Datum wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/blocked_dates/manage_dates.php`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { id }
        }
      );
      loadData();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleToggleWeekday = async (weekday) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = !weekday.is_active;
      
      await axios.put(
        `${API_BASE_URL}/blocked_dates/manage_weekdays.php`,
        {
          weekday_number: weekday.weekday_number,
          is_active: newStatus,
          reason: newStatus ? 'Geschlossen' : 'Geöffnet'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      loadData();
    } catch (err) {
      setError('Fehler beim Aktualisieren');
    }
  };

  const dateColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'blocked_date', 
      headerName: 'Datum', 
      width: 150,
      valueFormatter: (params) => dayjs(params.value).format('DD.MM.YYYY')
    },
    { field: 'reason', headerName: 'Grund', width: 300 },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 100,
      renderCell: (params) => <Switch checked={params.value === 1} disabled />
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 120,
      renderCell: (params) => (
        <IconButton 
          size="small" 
          color="error"
          onClick={() => handleDeleteBlockedDate(params.row.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventBusyIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Blockierte Tage & Feiertage
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Blockierte Daten" />
          <Tab label="Wochentage" />
        </Tabs>

        <CardContent>
          {/* Tab 0: Blocked Dates */}
          {tabValue === 0 && (
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Datum blockieren
              </Button>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={blockedDates}
                  columns={dateColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                />
              </Box>
            </Box>
          )}

          {/* Tab 1: Weekdays */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Wählen Sie die Wochentage aus, an denen KEINE Buchungen möglich sein sollen.
              </Typography>
              <Grid container spacing={2}>
                {weekdays.map((weekday) => (
                  <Grid item xs={12} sm={6} md={4} key={weekday.id}>
                    <Card 
                      sx={{ 
                        p: 2,
                        backgroundColor: weekday.is_active ? '#ffebee' : '#e8f5e9',
                        border: '2px solid',
                        borderColor: weekday.is_active ? '#ef5350' : '#66bb6a',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {weekday.weekday_name}
                          </Typography>
                          <Chip 
                            label={weekday.is_active ? 'Geschlossen' : 'Geöffnet'}
                            size="small"
                            color={weekday.is_active ? 'error' : 'success'}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={weekday.is_active === 1}
                              onChange={() => handleToggleWeekday(weekday)}
                              color="error"
                            />
                          }
                          label="Blockiert"
                        />
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Blocked Date Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Datum blockieren</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label="Datum auswählen"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { mb: 3 }
                  }
                }}
                sx={{ width: '100%', mb: 3 }}
              />
            </LocalizationProvider>
            
            <TextField
              fullWidth
              label="Grund"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Weihnachten, Betriebsferien"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleAddBlockedDate} 
            variant="contained"
            disabled={saving || !selectedDate}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlockedDates;

